import React, { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Cell, LineChart, Line, PieChart, Pie, Legend
} from "recharts";
import { 
  BarChart3, Activity, AlertTriangle, TrendingUp, ShoppingCart, 
  CheckCircle, Zap, ShieldAlert, ArrowRight, ExternalLink
} from "lucide-react";

// --- THEME ---
const THEME = {
  bg: "#0f172a",       // Deep Space
  sidebar: "#1e293b",  // Panel Grey
  card: "#1e293b",
  border: "#334155",
  textMain: "#f8fafc",
  textMuted: "#94a3b8",
  accent: "#3b82f6",   // Electric Blue
  positive: "#22c55e", // Growth Green
  negative: "#ef4444", // Risk Red
  warning: "#f59e0b",  // Warning Orange
  purple: "#8b5cf6",   // Strategy Purple
  
  // Business Unit Colors
  PSG: "#3b82f6",      // Paint Stores
  CBG: "#10b981",      // Consumer Brands
  PCG: "#f59e0b",      // Performance Coatings
};

// --- INTELLIGENCE DICTIONARY ---
const INTELLIGENCE_MAP = {
  // BUSINESS UNITS
  PSG: ["paint stores group", "psg", "stores", "residential repaint", "contractor", "diy"],
  CBG: ["consumer brands", "cbg", "retail", "lowe's", "home depot", "menards", "valspar"],
  PCG: ["performance coatings", "pcg", "industrial", "automotive", "refinish", "coil", "packaging"],

  // PRODUCT LINES
  ARCHITECTURAL: ["architectural", "latex", "interior", "exterior", "paint", "stain", "primer"],
  INDUSTRIAL: ["industrial", "wood", "coil", "automotive", "refinish", "marine", "aerospace", "protective"],
  
  // RISK CATEGORIES
  LEGAL: ["litigation", "lawsuit", "legal", "claims", "dispute", "settlement"],
  FINANCIAL_RISK: ["impairment", "default", "credit agreement", "bankruptcy", "debt", "liability"],
  OPERATIONAL_RISK: ["supply chain", "raw material", "shortage", "interruption", "labor"],

  // STRATEGIC THEMES
  DIGITAL: ["digital", "e-commerce", "online", "app", "tech", "automation", "data"],
  SUPPLY_CHAIN: ["supply chain", "inventory", "raw materials", "logistics", "freight"],
};

// --- COMPONENTS ---

function Sidebar({ activeView, setView }) {
  const items = [
    { id: "EXEC", label: "Executive Summary", icon: <Activity size={18} /> },
    { id: "BU", label: "Business Unit Decoder", icon: <BarChart3 size={18} /> },
    { id: "PRODUCT", label: "Product & Market Radar", icon: <ShoppingCart size={18} /> },
    { id: "STAR", label: "STAR Opportunities", icon: <CheckCircle size={18} /> },
    { id: "RISK", label: "Risk & Safety Monitor", icon: <ShieldAlert size={18} /> },
  ];

  return (
    <div style={{ width: 260, background: THEME.sidebar, borderRight: `1px solid ${THEME.border}`, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: 24, fontSize: 18, fontWeight: 900, color: THEME.textMain, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ background: THEME.accent, width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>⚡</div>
        STAR/INTEL
      </div>
      <div style={{ flex: 1, padding: "0 12px" }}>
        {items.map(item => (
          <button
            key={item.id}
            onClick={() => setView(item.id)}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 12,
              padding: "12px 16px",
              background: activeView === item.id ? `${THEME.accent}20` : "transparent",
              color: activeView === item.id ? THEME.accent : THEME.textMuted,
              border: "none", borderRadius: 8,
              cursor: "pointer", marginBottom: 4,
              fontWeight: activeView === item.id ? 700 : 500,
              textAlign: "left"
            }}
          >
            {item.icon} {item.label}
          </button>
        ))}
      </div>
      <div style={{ padding: 24, borderTop: `1px solid ${THEME.border}` }}>
        <div style={{ fontSize: 11, color: THEME.textMuted }}>Target Account:</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: THEME.textMain }}>Sherwin-Williams (SHW)</div>
      </div>
    </div>
  );
}

function InsightCard({ title, value, sub, color, icon }) {
  return (
    <div style={{ background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase' }}>{title}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: THEME.textMain, marginTop: 4 }}>{value}</div>
          <div style={{ fontSize: 11, color: color, marginTop: 4 }}>{sub}</div>
        </div>
        <div style={{ color: color, opacity: 0.8 }}>{icon}</div>
      </div>
    </div>
  );
}

// UPDATE: Added clickable link logic here
function EvidenceFeed({ signals, title, color = THEME.accent }) {
  if(!signals.length) return <div style={{padding:20, color: THEME.textMuted, fontSize: 13}}>No signals detected for this view.</div>;
  
  return (
    <div style={{ background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: 12, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${THEME.border}`, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Zap size={16} color={color} fill={color} /> {title}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', maxHeight: 400 }}>
        {signals.map((s, i) => (
          <div key={i} style={{ padding: "16px 20px", borderBottom: `1px solid ${THEME.border}` }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: `${THEME.textMuted}20`, color: THEME.textMuted }}>{s.filing_date}</span>
              <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: `${color}20`, color: color }}>{s.signal.toUpperCase()}</span>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: THEME.textMain }}>
              "{s.snippet.slice(0, 140)}..."
            </div>
            {/* Clickable Link */}
            {s.filing_url && (
              <a 
                href={s.filing_url} 
                target="_blank" 
                rel="noreferrer" 
                style={{ 
                  display: 'flex', alignItems: 'center', gap: 4, 
                  marginTop: 8, fontSize: 11, color: THEME.accent, textDecoration: 'none', fontWeight: 600 
                }}
              >
                View Filing <ExternalLink size={10} />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StarOpportunity({ title, trigger, action, impact }) {
  return (
    <div style={{ background: `linear-gradient(90deg, ${THEME.card} 0%, ${THEME.bg} 100%)`, border: `1px solid ${THEME.border}`, borderLeft: `4px solid ${THEME.purple}`, borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: THEME.textMain }}>{title}</div>
        <div style={{ fontSize: 11, fontWeight: 800, color: THEME.purple, background: `${THEME.purple}20`, padding: "4px 8px", borderRadius: 4 }}>ABM PLAY</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: THEME.textMuted, marginBottom: 4 }}>THE TRIGGER</div>
          <div style={{ fontSize: 13, color: THEME.textMain }}>{trigger}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: THEME.textMuted, marginBottom: 4 }}>THE ACTION</div>
          <div style={{ fontSize: 13, color: THEME.textMain }}>{action}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: THEME.textMuted, marginBottom: 4 }}>THE IMPACT</div>
          <div style={{ fontSize: 13, color: THEME.textMain }}>{impact}</div>
        </div>
      </div>
    </div>
  );
}

// --- MAIN LOGIC ---

export default function App() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("EXEC");
  const [status, setStatus] = useState("Initializing Intelligence Engine...");

  useEffect(() => {
    fetch("/default_signals.csv")
      .then(res => res.ok ? res.text() : Promise.reject("File missing"))
      .then(csvText => {
        Papa.parse(csvText, {
          header: true, skipEmptyLines: true,
          complete: (res) => {
            setRows((res.data || []).filter(r => r.signal && r.filing_date));
            setStatus("Ready");
          }
        });
      })
      .catch(err => setStatus("Error: Please upload default_signals.csv to public/"));
  }, []);

  const data = useMemo(() => {
    if (!rows.length) return null;
    const sorted = [...rows].sort((a,b) => new Date(b.filing_date) - new Date(a.filing_date));
    
    const filterByKeywords = (keywords) => sorted.filter(r => 
      keywords.some(k => r.snippet.toLowerCase().includes(k) || r.signal.toLowerCase().includes(k))
    );

    const psg = filterByKeywords(INTELLIGENCE_MAP.PSG);
    const cbg = filterByKeywords(INTELLIGENCE_MAP.CBG);
    const pcg = filterByKeywords(INTELLIGENCE_MAP.PCG);
    const architectural = filterByKeywords(INTELLIGENCE_MAP.ARCHITECTURAL);
    const industrial = filterByKeywords(INTELLIGENCE_MAP.INDUSTRIAL);
    const legalRisk = filterByKeywords(INTELLIGENCE_MAP.LEGAL);
    const finRisk = filterByKeywords(INTELLIGENCE_MAP.FINANCIAL_RISK);
    const opsRisk = filterByKeywords(INTELLIGENCE_MAP.OPERATIONAL_RISK);
    const digital = filterByKeywords(INTELLIGENCE_MAP.DIGITAL);
    const supply = filterByKeywords(INTELLIGENCE_MAP.SUPPLY_CHAIN);
    const finance = sorted.filter(r => r.signal === "pricing" || r.signal === "inflation" || r.signal === "capital allocation");

    const trendMap = {};
    sorted.forEach(r => {
      const d = r.filing_date.substring(0, 7);
      if(!trendMap[d]) trendMap[d] = { date: d, price: 0, inflation: 0 };
      if(r.signal.includes("pric")) trendMap[d].price++;
      if(r.signal === "inflation") trendMap[d].inflation++;
    });
    const trendData = Object.values(trendMap).sort((a,b) => a.date.localeCompare(b.date));

    const buDist = [
      { name: "Paint Stores", value: psg.length, color: THEME.PSG },
      { name: "Consumer", value: cbg.length, color: THEME.CBG },
      { name: "Performance", value: pcg.length, color: THEME.PCG },
    ];
    
    const prodDist = [
      { name: "Architectural (House)", value: architectural.length },
      { name: "Industrial (Factory/Auto)", value: industrial.length },
    ];

    return { sorted, psg, cbg, pcg, architectural, industrial, legalRisk, finRisk, opsRisk, digital, supply, finance, trendData, buDist, prodDist };
  }, [rows]);

  if (!data) return <div style={{ height: "100vh", background: THEME.bg, color: THEME.textMain, display: "flex", alignItems: "center", justifyContent: "center" }}>{status}</div>;

  const renderExec = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <InsightCard title="Total Intelligence" value={data.sorted.length} sub="Signals Analyzed" color={THEME.accent} icon={<Activity />} />
          <InsightCard title="Dominant BU" value="Paint Stores" sub="Highest Signal Vol." color={THEME.positive} icon={<TrendingUp />} />
          <InsightCard title="Risk Alert" value={data.finRisk.length} sub="Financial Flags" color={THEME.warning} icon={<AlertTriangle />} />
        </div>
        <div style={{ background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Macro Trend: The "Price over Volume" Story</div>
          <div style={{ height: 300 }}>
            <ResponsiveContainer>
              <AreaChart data={data.trendData}>
                <defs>
                  <linearGradient id="colorP" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={THEME.accent} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={THEME.accent} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="date" tick={{fontSize: 10, fill: THEME.textMuted}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize: 10, fill: THEME.textMuted}} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: THEME.card, borderColor: THEME.border }} />
                <Area type="monotone" dataKey="price" stroke={THEME.accent} fill="url(#colorP)" strokeWidth={2} name="Pricing Actions" />
                <Line type="monotone" dataKey="inflation" stroke={THEME.warning} strokeWidth={2} dot={false} name="Inflation Mentions" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <EvidenceFeed signals={data.finance.slice(0, 8)} title="Financial & Capital Feed" />
    </div>
  );

  const renderBU = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Share of Voice (BU)</div>
          <div style={{ height: 250 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.buDist} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                  {data.buDist.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Pie>
                <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{fontSize: 11, color: THEME.textMain}}/>
                <Tooltip contentStyle={{ backgroundColor: THEME.card, borderColor: THEME.border }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <EvidenceFeed signals={data.psg.slice(0, 6)} title="Latest PSG Signals" color={THEME.PSG} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Decoder: What they are saying per Unit</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
             <div style={{ background: THEME.bg, borderRadius: 8, padding: 16, borderTop: `4px solid ${THEME.PSG}` }}>
                <div style={{ fontSize: 14, fontWeight: 800 }}>Paint Stores (PSG)</div>
                <div style={{ fontSize: 11, color: THEME.textMuted, margin: "8px 0" }}>Focus: Pro, Contractor, Repaint</div>
                <div style={{ fontSize: 24, fontWeight: 800 }}>{data.psg.length}</div>
                <div style={{ fontSize: 10, color: THEME.textMuted }}>Signals</div>
             </div>
             <div style={{ background: THEME.bg, borderRadius: 8, padding: 16, borderTop: `4px solid ${THEME.CBG}` }}>
                <div style={{ fontSize: 14, fontWeight: 800 }}>Consumer (CBG)</div>
                <div style={{ fontSize: 11, color: THEME.textMuted, margin: "8px 0" }}>Focus: DIY, Retail Partners</div>
                <div style={{ fontSize: 24, fontWeight: 800 }}>{data.cbg.length}</div>
                <div style={{ fontSize: 10, color: THEME.textMuted }}>Signals</div>
             </div>
             <div style={{ background: THEME.bg, borderRadius: 8, padding: 16, borderTop: `4px solid ${THEME.PCG}` }}>
                <div style={{ fontSize: 14, fontWeight: 800 }}>Performance (PCG)</div>
                <div style={{ fontSize: 11, color: THEME.textMuted, margin: "8px 0" }}>Focus: Industrial, Auto, Coil</div>
                <div style={{ fontSize: 24, fontWeight: 800 }}>{data.pcg.length}</div>
                <div style={{ fontSize: 10, color: THEME.textMuted }}>Signals</div>
             </div>
          </div>
        </div>
        <EvidenceFeed signals={data.cbg.slice(0, 10)} title="Latest Consumer (CBG) Signals" color={THEME.CBG} />
      </div>
    </div>
  );

  const renderProduct = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
      <div>
        <div style={{ background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
           <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Product Mix Radar: Architectural vs. Industrial</div>
           <div style={{ height: 200 }}>
             <ResponsiveContainer>
               <BarChart layout="vertical" data={data.prodDist}>
                 <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                 <XAxis type="number" hide />
                 <YAxis dataKey="name" type="category" width={150} tick={{fontSize: 12, fill: THEME.textMain}} axisLine={false} tickLine={false} />
                 <Tooltip contentStyle={{ backgroundColor: THEME.card, borderColor: THEME.border }} />
                 <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                   <Cell fill={THEME.PSG} />
                   <Cell fill={THEME.PCG} />
                 </Bar>
               </BarChart>
             </ResponsiveContainer>
           </div>
           <div style={{ marginTop: 16, fontSize: 13, color: THEME.textMuted, lineHeight: 1.5 }}>
             <strong>Analysis:</strong> "Architectural" mentions dominate, aligning with the Paint Stores Group focus. Industrial mentions are significant but secondary, suggesting the core growth engine remains House/Building paint over factory coatings.
           </div>
        </div>
        <EvidenceFeed signals={data.architectural} title="Architectural Product Signals" color={THEME.PSG} />
      </div>
      <EvidenceFeed signals={data.industrial} title="Industrial Product Signals" color={THEME.PCG} />
    </div>
  );

  const renderRisk = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ padding: 12, background: `${THEME.negative}20`, border: `1px solid ${THEME.negative}`, borderRadius: 8, color: THEME.negative, fontWeight: 800, textAlign: 'center' }}>
          FINANCIAL RISKS ({data.finRisk.length})
        </div>
        <EvidenceFeed signals={data.finRisk} title="Impairment & Debt" color={THEME.negative} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ padding: 12, background: `${THEME.warning}20`, border: `1px solid ${THEME.warning}`, borderRadius: 8, color: THEME.warning, fontWeight: 800, textAlign: 'center' }}>
          OPERATIONAL RISKS ({data.opsRisk.length})
        </div>
        <EvidenceFeed signals={data.opsRisk} title="Supply & Labor" color={THEME.warning} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ padding: 12, background: `${THEME.purple}20`, border: `1px solid ${THEME.purple}`, borderRadius: 8, color: THEME.purple, fontWeight: 800, textAlign: 'center' }}>
          LEGAL RISKS ({data.legalRisk.length})
        </div>
        <EvidenceFeed signals={data.legalRisk} title="Litigation & Disputes" color={THEME.purple} />
      </div>
    </div>
  );

  const renderStar = () => (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>STAR ABM Opportunities</h2>
        <p style={{ color: THEME.textMuted }}>Automated plays based on signal triangulation.</p>
      </div>

      <StarOpportunity 
        title="Supply Chain Agility Pitch"
        trigger={`Found ${data.supply.length} mentions of 'Inventory' or 'Supply Chain' pressure.`}
        action="Deploy 'Resilient Operations' case study to COO/VP Ops."
        impact="Positions STAR as the partner that turns supply chain data into customer promises."
      />

      <StarOpportunity 
        title="Digital Transformation Audit"
        trigger={`Mentions of 'System Upgrade' and 'E-commerce' detected (${data.digital.length} signals).`}
        action="Propose a 2-week audit of their Pro App vs. Competitors."
        impact="Opens door for digital product work in the Paint Stores Group (PSG)."
      />

      <StarOpportunity 
        title="Margin Defense Campaign"
        trigger="High frequency of 'Pricing' and 'Inflation' keywords (detected in Macro Trend)."
        action="Pitch 'Value-Based Selling' enablement materials for Reps."
        impact="Helps them justify price hikes to their customers without losing volume."
      />

      <div style={{ marginTop: 32 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Supporting Data for Opportunities</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <EvidenceFeed signals={data.digital} title="Digital & Tech Signals" />
          <EvidenceFeed signals={data.supply} title="Supply Chain & Inventory Signals" />
        </div>
      </div>
    </div>
  );

  // --- LAYOUT ---
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: THEME.bg, color: THEME.textMain, fontFamily: "Inter, sans-serif" }}>
      <Sidebar activeView={view} setView={setView} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ height: 64, borderBottom: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", padding: "0 32px", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 700 }}>
            {view === "EXEC" && "Executive Summary"}
            {view === "BU" && "Business Unit Decoder"}
            {view === "STAR" && "Strategic Opportunity Engine"}
            {view === "PRODUCT" && "Product & Market Radar"}
            {view === "RISK" && "Risk & Safety Monitor"}
          </div>
          <div style={{ fontSize: 12, color: THEME.textMuted }}>Auto-Synced • {data.sorted.length} Signals</div>
        </div>
        <div style={{ padding: 32, flex: 1, overflowY: "auto" }}>
          {view === "EXEC" && renderExec()}
          {view === "BU" && renderBU()}
          {view === "PRODUCT" && renderProduct()}
          {view === "STAR" && renderStar()}
          {view === "RISK" && renderRisk()}
        </div>
      </div>
    </div>
  );
}
